/**
 * Bug Report Button - Floating lifesaver button for reporting issues
 *
 * Shows during subscription flows to help users report problems
 * Captures diagnostic context automatically
 */

import { useState } from 'react'
import { Modal } from './Modal'

interface BugReportButtonProps {
  errorContext?: {
    message?: string
    code?: string
    stack?: string
  }
}

export function BugReportButton({ errorContext }: BugReportButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitError('')

    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

      const response = await fetch(`${API_URL}/api/bug-reports`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          description: description.trim(),
          current_url: window.location.href,
          user_agent: navigator.userAgent,
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          error_message: errorContext?.message,
          error_code: errorContext?.code
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to submit bug report')
      }

      // Success!
      setSubmitSuccess(true)
      setDescription('')

      // Auto-close modal after 2 seconds
      setTimeout(() => {
        setIsOpen(false)
        setSubmitSuccess(false)
      }, 2000)

    } catch (error) {
      console.error('Bug report submission error:', error)
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit bug report')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Floating Lifesaver Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-gray-800 hover:bg-gray-700 text-white px-4 py-3 rounded-full shadow-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium"
        aria-label="Report an issue"
      >
        <span className="text-xl">🛟</span>
        <span className="hidden sm:inline">Bug?</span>
      </button>

      {/* Bug Report Modal */}
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
          setSubmitError('')
          setSubmitSuccess(false)
        }}
        title="Report an Issue"
        maxWidth="max-w-lg"
      >
        {submitSuccess ? (
          // Success State
          <div className="p-6">
            <div className="text-center py-8">
              <div className="text-6xl mb-4">✅</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Thank You!
              </h3>
              <p className="text-gray-600">
                Your bug report has been submitted. We'll look into it!
              </p>
            </div>
          </div>
        ) : (
          // Form State
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              {/* Error Message Display */}
              {errorContext && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                    Error Detected:
                  </p>
                  <p className="text-sm text-yellow-700 font-mono">
                    {errorContext.code || errorContext.message || 'Unknown error'}
                  </p>
                </div>
              )}

              {/* Description Field */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  What happened? *
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  rows={6}
                  placeholder="Describe the issue you encountered..."
                  disabled={submitting}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Please be as specific as possible
                </p>
              </div>

              {/* Auto-captured Context Info */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs text-gray-600">
                  Current error logs will also be recorded as well.
                </p>
              </div>

              {/* Submit Error */}
              {submitError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{submitError}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || !description.trim()}
                className="w-full py-3 bg-pink-600 text-white rounded-lg font-semibold hover:bg-pink-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>

              <p className="text-xs text-center text-gray-500">
                We'll investigate your report and work on fixing the issue
              </p>

              {/* Divider */}
              <div className="border-t border-gray-200 pt-4 mt-2">
                <p className="text-xs text-center text-gray-500 mb-3">
                  Try logging out and signing in with a different account?
                </p>
                <button
                  type="button"
                  onClick={() => {
                    localStorage.removeItem('access_token')
                    localStorage.removeItem('refresh_token')
                    localStorage.removeItem('auth_token')
                    localStorage.removeItem('user')
                    window.location.reload()
                  }}
                  className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  Log out & Re-login
                </button>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </>
  )
}
